import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SectionHeading } from "@/components/SectionHeading";
import { ChapterArt } from "@/components/ChapterArt";
import { siteMeta, dimensions, experience } from "@/content/siteContent";

export default function About() {
  return (
    <div data-testid="about-page">
      <header className="bg-sand">
        <div className="relative mx-auto max-w-6xl px-6 pb-20 pt-20 sm:pt-28">
          <div className="hidden sm:block" data-testid="page-art-about-wrapper">
            <ChapterArt testId="page-art-about" />
          </div>
          <Reveal>
            <p className="eyebrow">About</p>
            <h1 className="mt-6 max-w-4xl text-4xl font-black leading-tight tracking-tighter sm:text-5xl">
              {siteMeta.positioning}
            </h1>
          </Reveal>
        </div>
      </header>

      <section className="bg-white">
        <div className="mx-auto max-w-6xl px-6 py-20 sm:py-28">
          <Reveal>
            <SectionHeading index="01" label="Profile" title="Three connected dimensions" />
          </Reveal>
          <div className="mt-14 grid gap-12 md:grid-cols-3">
            {dimensions.map((d, i) => (
              <Reveal key={d.title} delay={i * 0.1}>
                <div className="border-t-2 border-ink pt-6">
                  <h2 className="text-base font-bold tracking-tight sm:text-lg">{d.title}</h2>
                  <p className="mt-4 text-sm leading-relaxed text-muted sm:text-base">{d.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-sand" data-testid="experience-section">
        <div className="mx-auto max-w-6xl px-6 py-20 sm:py-28">
          <Reveal>
            <SectionHeading index="02" label="Experience" title="Where I've worked" />
            <p className="mt-6 max-w-2xl text-sm leading-relaxed text-muted sm:text-base">
              Seven-plus years across strategy, market research, competitive intelligence, analytics,
              reporting transformation, and process improvement.
            </p>
          </Reveal>
          <div className="mt-12 divide-y divide-ink/10 border-y border-ink/10">
            {experience.map((e, i) => (
              <Reveal key={e.org} delay={i * 0.05}>
                <div className="grid gap-3 py-8 sm:grid-cols-[220px_1fr] sm:gap-10" data-testid={`experience-${e.org.toLowerCase()}`}>
                  <div>
                    <h3 className="text-lg font-bold tracking-tight">{e.org}</h3>
                    <p className="mt-2 text-xs text-muted">{e.dates}</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold tracking-tight sm:text-base">{e.role}</p>
                    <p className="mt-2 text-sm leading-relaxed text-muted sm:text-base">{e.detail}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white" data-testid="cv-section">
        <div className="mx-auto max-w-6xl px-6 py-20 sm:py-28">
          <Reveal>
            <SectionHeading index="03" label="CV" title="The full picture, on paper" />
            <p className="mt-6 max-w-2xl text-sm leading-relaxed text-muted sm:text-base">
              Roles, sectors, selected achievements, and recognition — all in one document.
            </p>
            <a
              href="/Kaithwar_Epsa_Resume.pdf"
              download
              className="mt-8 inline-flex items-center gap-2 rounded-full border border-ink px-6 py-3 text-sm font-medium transition-colors duration-300 hover:bg-ink hover:text-sand"
              data-testid="cv-download-button"
            >
              Download CV (PDF)
            </a>
            <div>
              <Link
                to="/contact"
                className="editorial-link mt-10 inline-flex items-center gap-2 text-base font-semibold"
                data-testid="about-contact-link"
              >
                Get in touch <ArrowRight size={16} aria-hidden="true" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
